/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { DockerContainerStats } from "@/api/docker";
import { TrafficPoint } from "@/components/snmp/SNMPTrafficChart";

export interface ResourcePoint {
  time: string;
  cpuPercent: number;
  memUsageBytes: number;
}

// CPU%/memory are already point-in-time gauges (not cumulative counters), so
// this is a direct chronological passthrough -- no delta computation needed.
export function toResourcePoints(stats: DockerContainerStats[]): ResourcePoint[] {
  return [...(stats ?? [])]
    .reverse()
    .map((s) => ({ time: s.createdAt, cpuPercent: s.cpuPercent, memUsageBytes: s.memUsageBytes }));
}

// Network rx/tx are cumulative counters since container start, so this needs
// the same delta-over-time rate computation as SNMP interface counters.
export function toNetworkTrafficPoints(stats: DockerContainerStats[]): TrafficPoint[] {
  if (!stats || stats.length < 2) return [];

  const chronological = [...stats].reverse();
  const points: TrafficPoint[] = [];

  for (let i = 1; i < chronological.length; i++) {
    const prev = chronological[i - 1];
    const cur = chronological[i];
    const dtSeconds =
      (new Date(cur.createdAt).getTime() - new Date(prev.createdAt).getTime()) / 1000;
    if (dtSeconds <= 0) continue;

    const rxDelta = cur.netRxBytes - prev.netRxBytes;
    const txDelta = cur.netTxBytes - prev.netTxBytes;

    points.push({
      time: cur.createdAt,
      in: rxDelta > 0 ? rxDelta / dtSeconds : 0,
      out: txDelta > 0 ? txDelta / dtSeconds : 0,
    });
  }

  return points;
}
